import React from 'react';
import { observer, inject } from 'mobx-react';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import * as DataList from 'components/DataList';
import * as Schedule from 'components/Tables/ScheduleList';

// A client's jobs (client page, Jobs tab), laid out like the Jobs list
// (components/Tables/Jobs). Every row belongs to this client, so the client
// column becomes the job's location.
const LOCATION_COLUMN = { area: 'where', label: 'Location', width: 'minmax(180px, 1.3fr)', icon: 'map-marker' };

const COLUMNS = Schedule.jobColumns(false).map((column) => (column.area === 'client' ? LOCATION_COLUMN : column));

const MAIN_CARDS = Schedule.jobCards(false);
const CARDS = {
  ...MAIN_CARDS,
  areas: MAIN_CARDS.areas.map((row) => row.replace(/\bclient\b/g, 'where')),
};

// Sum of the column minimums plus row padding and gaps; narrower lists use cards.
const MIN_TABLE_WIDTH = 1330;

@inject('viewStore', 'jobStore') @observer
class ClientJobsTable extends React.Component {

  // The store starts out not loading; until this tab has asked for its jobs,
  // show the skeleton rather than a flash of the empty state.
  requested = false;

  componentDidMount() {
    this.requested = true;
    this.props.viewStore.clientJobSearch.where.client_id = this.props.client.attributes.id;
    this.props.jobStore.loadClientJobs();
  }

  onPaginationChange(newPage) {
    let {
      viewStore: {
        clientJobSearch
      }
    } = this.props;
    this.props.viewStore.clientJobSearch = {
      ...clientJobSearch,
      page: newPage
    };
    this.props.jobStore.loadClientJobs();
  }

  render() {
    const {
      client,
      viewStore: {
        clientJobSearch,
        jobTab: {
          section
        }
      },
      jobStore: {
        clientJobs: { objects, isLoading, isError, meta }
      }
    } = this.props;

    // The store's client jobs are shared between client pages; rows left over
    // from another client aren't shown while this client's jobs load.
    const first = objects[0];
    const stale = Boolean(first && first.attributes.client && first.attributes.client.id
      && first.attributes.client.id !== client.attributes.id);

    if ((isLoading || !this.requested) && (objects.length === 0 || stale)) {
      return (
        <DataList.Loading columns={COLUMNS} cards={CARDS} minTableWidth={MIN_TABLE_WIDTH} noun="jobs" rows={5} />
      );
    }
    if (isError) {
      return <DataList.LoadError noun="jobs" onRetry={() => this.onPaginationChange((meta && meta.page) || 1)} />;
    }
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="jobs"
          message="Jobs you schedule for this client will show up here."
        />
      );
    }

    const pagination = meta && meta.total_pages > 1 && (
      <Footer>
        <div className="bp3-navbar-group bp3-align-left">
          <Pagination
            value={meta.page}
            total={meta.total_pages}
            onChange={this.onPaginationChange.bind(this)}
          />
        </div>
      </Footer>
    );

    return (
      <React.Fragment>
        <DataList.List
          preferTable
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          total={(meta && meta.total_records) || objects.length}
          noun="jobs"
          note={DataList.sortNote(clientJobSearch.order)}
        >
          {objects.map((job) => {
            const attributes = job.attributes;
            const link = `/dashboard/jobs/${job.id}/${section}`;
            const createdBy = attributes.created_by && attributes.created_by.full_name;
            const agreement = attributes.service_agreement && attributes.service_agreement.number;
            const project = attributes.project && attributes.project.name;
            const address = attributes.location && attributes.location.full_address;
            return (
              <DataList.Row key={job.id} accent={Schedule.accentFor(attributes.service_type_color)}>
                <DataList.Cell area="number">
                  <Schedule.JobNumber
                    number={attributes.event_number}
                    link={link}
                    title={attributes.title}
                    serviceType={{ name: attributes.service_type_name, color: attributes.service_type_color }}
                  />
                </DataList.Cell>
                <DataList.Cell area="job">
                  <Schedule.JobTitle
                    title={attributes.title}
                    details={[
                      Schedule.poLabel(attributes.po_number),
                      agreement && `Agreement ${agreement}`,
                      project && `Project: ${project}`,
                    ]}
                  />
                </DataList.Cell>
                <DataList.Cell area="when">
                  <Schedule.When start={attributes.start_date} end={attributes.end_date} />
                </DataList.Cell>
                <DataList.Cell area="team">
                  <Schedule.Team assignments={attributes.assignments} />
                </DataList.Cell>
                <DataList.Cell area="where">
                  {address ? (
                    <DataList.Lines
                      primary={address}
                      secondary={<Schedule.Directions location={attributes.location} />}
                      strong={false}
                    />
                  ) : (
                    <DataList.Muted>No location</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="created">
                  <Schedule.Created date={attributes.created_at} by={createdBy} />
                </DataList.Cell>
                <DataList.Cell area="billing">
                  <Schedule.Billing invoiced={attributes.status === 'invoiced'} />
                </DataList.Cell>
                <DataList.Cell area="priority">
                  <Schedule.Priority priority={attributes.priority} />
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions items={[{ icon: 'eye-open', label: 'View job', href: link }]} />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        {pagination}
      </React.Fragment>
    );
  }
}

export default ClientJobsTable;
